import { z } from "zod/v4"
import { ShopBannerId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { Period, PeriodState } from "./common"
import { DocumentStatus } from "../common"

export const ShopBannerDisplayLocation = z.enum(["first", "list"]).describe("バナーの表示場所")
export type ShopBannerDisplayLocation = z.infer<typeof ShopBannerDisplayLocation>

export const ShopBannerDocumentMeta = z.object({
  managed_image_id: z.string().describe("バナー画像のID（ManagedImageId）"),
  priority: z.int().min(1).max(99999).describe("優先度（1-99999）"),
  display_location: ShopBannerDisplayLocation.describe("表示場所"),
  uri: z.string().describe("タップ時の遷移URI"),
  display_period: Period.describe("表示期間"),
  image_text: z.string().nullable().describe("画像上に表示するテキスト"),
  status: DocumentStatus.describe("バナーの状態"),
  planning_management_id: z.string().nullable().describe("企画管理用の Uniq ID（管理番号）。最大12文字。AES-CBC で暗号化された文字列が格納される。").optional(),
  planning_name: z.string().nullable().describe("企画管理用名称。最大50文字。AES-CBC で暗号化された文字列が格納される。").optional(),
})

export type ShopBannerDocumentMeta = z.infer<typeof ShopBannerDocumentMeta>

export const ShopBannerDocumentBody = z.intersection(
  ShopBannerDocumentMeta,
  z.object({
    reference_managed_image_ids: z.array(z.string()).describe("使用している画像のIDリスト（自動計算）"),
    display_period_state: PeriodState.describe("表示期間の状態（システム自動計算）"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type ShopBannerDocumentBody = z.infer<typeof ShopBannerDocumentBody>

export type ShopBannerDocument = ShopBannerDocumentBody & {
  id: ShopBannerId,
}

export class ShopBannerDocuments {

  static toDocumentData(banner: ShopBannerDocument): object {
    return {
      ...banner,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): ShopBannerDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: ShopBannerId(doc.id),
      managed_image_id: data["managed_image_id"],
      priority: data["priority"],
      display_location: data["display_location"],
      uri: data["uri"],
      display_period: data["display_period"],
      display_period_state: data["display_period_state"],
      reference_managed_image_ids: data["reference_managed_image_ids"] || [],
      image_text: data["image_text"],
      planning_management_id: data["planning_management_id"] ?? null,
      planning_name: data["planning_name"] ?? null,
      status: data["status"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreateShopBannerRequest = z.object({
  managedImageId: z.string().describe("バナー画像のID"),
  priority: z.int().min(1).max(99999).default(1).describe("優先度（1-99999）"),
  displayLocation: ShopBannerDisplayLocation.describe("表示場所"),
  uri: z.string().min(1).describe("タップ時の遷移URI"),
  displayPeriod: z.object({
    startAt: z.number().describe("表示開始日時 (UNIX Timestamp mills)"),
    endAt: z.number().nullable().describe("表示終了日時 (UNIX Timestamp mills、nullの場合は無期限)"),
  }).describe("表示期間"),
  imageText: z.string().max(50).nullable().optional().describe("画像上に表示するテキスト"),
  status: DocumentStatus.describe("バナーの状態"),
  planningManagementId: z.string().nullable().optional().describe("企画管理用の Uniq ID（管理番号）。AES-CBC で暗号化された文字列。平文は最大12文字。"),
  planningName: z.string().nullable().optional().describe("企画管理用名称。AES-CBC で暗号化された文字列。平文は最大50文字。"),
})

export type CreateShopBannerRequest = z.infer<typeof CreateShopBannerRequest>

export const UpdateShopBannerRequest = z.object({
  managedImageId: z.string().optional().describe("バナー画像のID"),
  priority: z.int().min(1).max(99999).optional().describe("優先度（1-99999）"),
  displayLocation: ShopBannerDisplayLocation.optional().describe("表示場所"),
  uri: z.string().min(1).optional().describe("タップ時の遷移URI"),
  displayPeriod: z.object({
    startAt: z.number().describe("表示開始日時 (UNIX Timestamp mills)"),
    endAt: z.number().nullable().describe("表示終了日時 (UNIX Timestamp mills、nullの場合は無期限)"),
  }).optional().describe("表示期間"),
  imageText: z.string().max(50).nullable().optional().describe("画像上に表示するテキスト"),
  status: DocumentStatus.optional().describe("バナーの状態"),
  planningManagementId: z.string().nullable().optional().describe("企画管理用の Uniq ID（管理番号）。AES-CBC で暗号化された文字列。平文は最大12文字。"),
  planningName: z.string().nullable().optional().describe("企画管理用名称。AES-CBC で暗号化された文字列。平文は最大50文字。"),
})

export type UpdateShopBannerRequest = z.infer<typeof UpdateShopBannerRequest>
